import React, { useState } from 'react'
import { toast } from '@/lib/toasts/api'
import { BusinessTripModel } from '@/app/models/businessTripModel'
import { CountryModel } from '@/app/models/timesheet/countryModel'
import { TaskType } from '@/app/constants/taskType'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useTask } from '@/modules/shared/context/TaskContext'
import useBusinessTripService, {
  TripBulkOperation,
  TripBulkOperationParams,
} from '@/modules/drivers/services/BusinessTripService'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'
import {
  getCurrencyTableIssueDetails,
  getCurrencyTableIssueFromApiError,
} from '@/modules/shared/utils/currencyTableIssue'
import {
  isTaskRateLimited,
  startTrackedTask,
} from '@/modules/shared/utils/startTrackedTask'
import { taskScopeKey } from '@/modules/shared/utils/taskScopeKey'
import type { ActionKey } from '../components/tables/BusinessTripContextMenu'

export type DialogState = {
  type: ActionKey | null
  trip: BusinessTripModel | null
}

/**
 * Wszystkie operacje menu liczy serwer na delegacjach wczytanych z bazy - parytet z WPF
 * TripsListViewModel. Jedna delegacja idzie od razu, kilka - jednym zadaniem
 * w fileprocessorze, żeby setki delegacji nie wisiały na otwartej karcie.
 */
const useBusinessTripContextMenuActions = ({
  companyId,
  driverId: fixedDriverId,
  selectedIds = [],
  allTrips,
  onRefresh,
}: {
  companyId: string
  /** Gdy ustawione (widok jednego kierowcy), wszystkie operacje używają tego ID. Gdy puste — ID brane z każdej delegacji (widok grupowy). */
  driverId?: string
  /** Zaznaczone delegacje (multi-select). Puste = akcja dotyczy tylko klikniętej. */
  selectedIds?: number[]
  allTrips: BusinessTripModel[]
  onRefresh: () => void
}) => {
  const { t } = useLocalize()
  const { completedTasks } = useTask()
  const invalidateDriverRequiredActions = useInvalidateDriverRequiredActions()
  const getDriverIdForTrip = (trip: BusinessTripModel): string => {
    if (fixedDriverId) return fixedDriverId
    return String(trip.driver?.id ?? trip.driver ?? '')
  }
  const {
    refreshTripCosts,
    refreshTripCostsBatchTask,
    runTripBulkOperation,
    startTripBulkOperationTask,
  } = useBusinessTripService()

  const [dialogState, setDialogState] = useState<DialogState>({
    type: null,
    trip: null,
  })
  const [taskIds, setTaskIds] = useState<string[]>([])

  React.useEffect(() => {
    const finished = taskIds.filter((taskId) =>
      completedTasks.some((task) => task.id === taskId),
    )
    if (finished.length === 0) {
      return
    }

    invalidateDriverRequiredActions()
    onRefresh()
    setTaskIds((ids) => ids.filter((taskId) => !finished.includes(taskId)))
  }, [completedTasks, taskIds, invalidateDriverRequiredActions, onRefresh])

  const closeDialog = () => setDialogState({ type: null, trip: null })

  const getTripsToProcess = (trip: BusinessTripModel): BusinessTripModel[] => {
    const tripId =
      typeof trip.id === 'string' ? parseInt(trip.id, 10) : (trip.id as number)
    if (selectedIds.includes(tripId) && selectedIds.length > 1) {
      return allTrips.filter((t) => {
        const id =
          typeof t.id === 'string' ? parseInt(t.id, 10) : (t.id as number)
        return selectedIds.includes(id)
      })
    }
    return [trip]
  }

  const extractErrorMessage = (error: any): string => {
    const data = error?.response?.data
    const status = error?.response?.status
    // Odpowiedzi o braku kursu niosa w `message` sama date kursu - bez tego
    // uzytkownik dostawal w toascie goly "2026-09-10" albo komunikat HTTP z NBP.
    const currencyIssue = getCurrencyTableIssueFromApiError(error)
    if (currencyIssue) {
      return getCurrencyTableIssueDetails(currencyIssue, t)
    }
    if (data) {
      if (data.errors) {
        const messages = (
          Object.values(data.errors as Record<string, string[]>) as string[][]
        )
          .flat()
          .join('; ')
        if (messages) return messages
      }
      if (
        data.title &&
        data.title !== 'One or more validation errors occurred.'
      )
        return data.title
      if (data.message) return data.message
      if (typeof data === 'string' && data.length < 300) return data
    }
    if (status === 400) return t('error.http-400')
    if (status === 401) return t('error.http-401')
    if (status === 403) return t('error.http-403')
    if (status === 404) return t('error.http-404')
    if (status === 500) return t('error.http-500')
    return error?.message || t('error.unknown')
  }

  const runOperation = async (
    operation: TripBulkOperation | 'refreshCosts',
    trips: BusinessTripModel[],
    params: TripBulkOperationParams = {},
  ) => {
    const items = trips
      .map((trip) => ({
        tripId: Number(trip.id),
        driverId: Number(getDriverIdForTrip(trip)),
      }))
      .filter(
        (item) => Number.isFinite(item.tripId) && Number.isFinite(item.driverId),
      )
    if (items.length === 0) return

    if (items.length === 1) {
      try {
        if (operation === 'refreshCosts') {
          await refreshTripCosts({ companyId, ...items[0] })
        } else {
          const results = await runTripBulkOperation(companyId, {
            operation,
            trips: items,
            ...params,
          })
          if (operation === 'copyToTeam') {
            const created = results.reduce(
              (sum, result) => sum + (result.createdTrips ?? 0),
              0,
            )
            if (created === 0) {
              toast.info({ text: t('businessTrip.copy-to-team-nothing-copied') })
              onRefresh()
              return
            }
          }
        }
        toast.success({ text: t('businessTrip.operation-success') })
      } catch (error) {
        toast.error({
          title: t('businessTrip.operation-error'),
          text: extractErrorMessage(error),
        })
      }
      onRefresh()
      return
    }

    try {
      // Ten sam firmowy zakres co masowe odświeżenie kosztów: obie ścieżki zapisują
      // delegacje, więc serwer odrzuci drugą i podamy biegnące zadanie.
      const { taskId, adopted } = await startTrackedTask({
        companyId,
        type: TaskType.BusinessTrip,
        scopeKey: taskScopeKey.businessTripCostsRefresh(companyId),
        data: {
          operation:
            operation === 'refreshCosts' ? 'refreshCosts' : 'tripBulkOperation',
          progressCurrent: 0,
          progressTotal: items.length,
        },
        progressTotal: items.length,
        start: () =>
          operation === 'refreshCosts'
            ? refreshTripCostsBatchTask({ companyId, trips: items })
            : startTripBulkOperationTask(companyId, {
                operation,
                trips: items,
                ...params,
              }),
      })

      if (taskId) {
        setTaskIds((ids) => [...ids, taskId])
      }
      if (adopted) {
        toast.info({
          title: t('businessTrip.bulk-operation-in-progress'),
          text: t('businessTrip.bulk-operation-already-running'),
        })
      }
    } catch (error) {
      if (isTaskRateLimited(error)) {
        toast.warning({
          text: t('timesheet.too-many-tasks'),
          title: t('businessTrip.bulk-operation-in-progress'),
        })
        return
      }
      toast.error({
        title: t('businessTrip.operation-error'),
        text: extractErrorMessage(error),
      })
    }
  }

  const handleAction = (action: ActionKey, trip: BusinessTripModel) => {
    if (
      [
        'changeCountry',
        'changeSettlementMonth',
        'changeSettlementDate',
        'changeExchangeRateDate',
        'setVehicle',
      ].includes(action)
    ) {
      setDialogState({ type: action, trip })
      return
    }

    runOperation(action as TripBulkOperation | 'refreshCosts', getTripsToProcess(trip))
  }

  const confirmWith = (
    operation: TripBulkOperation,
    params: TripBulkOperationParams,
  ) => {
    const { trip } = dialogState
    if (!trip) return
    closeDialog()
    runOperation(operation, getTripsToProcess(trip), params)
  }

  const handleConfirmCountry = (country: CountryModel) =>
    confirmWith('changeCountry', { countryId: Number(country.id) })

  const handleConfirmSettlementMonth = (monthStr: string) =>
    confirmWith('changeSettlementMonth', { settlementMonth: `${monthStr}-01` })

  const handleConfirmSettlementDate = (dateStr: string) =>
    confirmWith('changeSettlementDate', { calculationDate: dateStr })

  const handleConfirmExchangeRateDate = (
    dateStr: string | null,
    useDefaultDate: boolean,
  ) =>
    confirmWith('changeExchangeRateDate', {
      exchangeRateDate: useDefaultDate ? null : dateStr,
      useDefaultExchangeRateDate: useDefaultDate,
    })

  const handleConfirmVehicle = (vehicleId: string) =>
    confirmWith('setVehicle', { vehicleId: Number(vehicleId) })

  return {
    dialogState,
    handleAction,
    closeDialog,
    handleConfirmCountry,
    handleConfirmSettlementMonth,
    handleConfirmSettlementDate,
    handleConfirmExchangeRateDate,
    handleConfirmVehicle,
  }
}

export default useBusinessTripContextMenuActions
